/**
 * PagedJS Service
 * Provides centralized management of PagedJS script for print templates
 * Eliminates CDN dependency by serving PagedJS locally
 */

export class PagedJSService {
  private static instance: PagedJSService;
  private pagedJsContent: string | null = null;
  private loadPromise: Promise<string> | null = null;

  private constructor() {}

  static getInstance(): PagedJSService {
    if (!PagedJSService.instance) {
      PagedJSService.instance = new PagedJSService();
    }
    return PagedJSService.instance;
  }

  /**
   * Load PagedJS content from local file
   * Uses caching to avoid multiple fetches
   */
  async loadPagedJSContent(): Promise<string> {
    // Return cached content if available
    if (this.pagedJsContent) {
      return this.pagedJsContent;
    }

    // Return existing load promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading
    this.loadPromise = this.fetchPagedJSContent();

    try {
      this.pagedJsContent = await this.loadPromise;
      return this.pagedJsContent;
    } finally {
      this.loadPromise = null;
    }
  }

  /**
   * Fetch PagedJS content from local server
   */
  private async fetchPagedJSContent(): Promise<string> {
    // Fetch from local served path - no CDN fallback
    const response = await fetch('/pagedjs/paged.polyfill.js');

    if (!response.ok) {
      throw new Error(`Failed to load PagedJS from local path: ${response.status} ${response.statusText}. Ensure paged.polyfill.js is available in public/pagedjs/ directory.`);
    }

    const content = await response.text();
    console.log('Successfully loaded local PagedJS (size: ' + content.length + ' bytes)');
    return content;
  }

  /**
   * Get PagedJS script tag for embedding in print documents
   *
   * @param options Configuration options
   * @param options.inline Whether to inline the script content (default: false)
   * @returns HTML script tag for PagedJS
   */
  getPagedJSScriptTag(options: {
    inline?: boolean;
  } = {}): string {
    const { inline = false } = options;

    // If inline is requested and we have content
    if (inline && this.pagedJsContent) {
      return `<script>${this.pagedJsContent}</script>`;
    }

    // Use local file reference - no CDN fallback
    return '<script src="/pagedjs/paged.polyfill.js"></script>';
  }

  /**
   * Get PagedJS script tag for async loading
   * Used when we want to load and inline PagedJS content dynamically
   */
  async getPagedJSScriptTagAsync(): Promise<string> {
    const content = await this.loadPagedJSContent();

    // Return inline script with loaded content
    return this.getPagedJSScriptTag({
      inline: true
    });
  }
}

// Export singleton instance
export const pagedJSService = PagedJSService.getInstance();